import useOnMount from '@mui/utils/useOnMount';
import { useRouter } from 'next/router';
import { useState } from 'react';
import ActionModal from '@/components/ActionModal/ActionModal';
import FormResultDialog, {
  type DialogContent,
} from '@/components/FormResultDialog/FormResultDialog';
import type { ActionModalCF } from '@/types';
import { appendQueryParams } from '@/utils/url';

export type LeadFormResultType = {
  isEligible: boolean;
  params: URLSearchParams | null;
  modalData: {
    success: ActionModalCF | DialogContent | undefined;
    failure: ActionModalCF | DialogContent | undefined;
  };
  redirectData: {
    success: string | undefined;
    failure: string | undefined;
  };
} | null;

type ModalResultType = {
  isEligible: boolean;
  params: URLSearchParams | null;
  data: {
    success: ActionModalCF | DialogContent;
    failure?: ActionModalCF | DialogContent;
  };
};

type RedirectResultType = {
  isEligible: boolean;
  params: URLSearchParams | null;
  data: {
    success: string;
    failure: string | undefined;
  };
};

const isActionModal = (
  object: ActionModalCF | DialogContent
): object is ActionModalCF => {
  return 'sys' in object;
};

export const LeadFormResult = ({ result }: { result: LeadFormResultType }) => {
  if (!result) return null;

  if (result.redirectData.success) {
    const props = {
      isEligible: result.isEligible,
      params: result.params,
      data: result.redirectData,
    } as RedirectResultType;

    return <LeadFormRedirectResultHandler {...props} />;
  } else {
    const props = {
      isEligible: result.isEligible,
      params: result.params,
      data: result.modalData,
    } as ModalResultType;

    return <LeadFormModalResultHandler {...props} />;
  }
};

const LeadFormRedirectResultHandler = ({
  isEligible,
  data,
  params,
}: RedirectResultType) => {
  const router = useRouter();

  useOnMount(() => {
    let redirectUrl = data.success;

    if (!isEligible && data.failure) {
      redirectUrl = data.failure;
    }

    if (params) {
      redirectUrl = appendQueryParams(redirectUrl, params);
    }

    router.push(redirectUrl);
  });

  return null;
};

const LeadFormModalResultHandler = ({
  isEligible,
  data,
  params,
}: ModalResultType) => {
  const [isOpen, setIsOpen] = useState(true);

  let content = data.success;

  if (!isEligible && data.failure) {
    content = data.failure;
  }

  if (isActionModal(content)) {
    const contentClone = JSON.parse(JSON.stringify(content));
    const cta = contentClone.fields.cta;
    const secondaryCta = contentClone.fields.secondaryCta;

    if (cta?.fields.url && params) {
      contentClone.fields.cta.fields.url = appendQueryParams(
        cta.fields.url,
        params
      );
    }
    if (secondaryCta?.fields.url && params) {
      contentClone.fields.secondaryCta.fields.url = appendQueryParams(
        secondaryCta.fields.url,
        params
      );
    }

    return (
      <ActionModal
        open={isOpen}
        onClose={() => setIsOpen(false)}
        modalContent={contentClone}
      />
    );
  }

  return (
    <FormResultDialog
      open={isOpen}
      onClose={() => setIsOpen(false)}
      dialogContent={content}
    />
  );
};
